import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "../../lib/api";
import { Window, Dialog, ConfirmDialog, EmptyState, Loading, StatusBadge } from "../../components/VB";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Upload } from "lucide-react";

const emptyForm = {
  no_karyawan: "", nama_lengkap: "", tempat_lahir: "", tanggal_lahir: "",
  alamat: "", nik: "", no_hp: "", email: "", pendidikan: "",
  divisi_id: "", jabatan_id: "", masa_awal_masuk: "",
  tanggal_mulai_kontrak: "", tanggal_selesai_kontrak: "",
  status: "Aktif", foto_path: "",
  metode_gaji: "Transfer", bank_nama: "", bank_rekening: "", bank_atas_nama: "",
  jenis_kelamin: "Laki-laki", agama: ""
};

export default function Karyawan() {
  const [items, setItems] = useState([]);
  const [divisi, setDivisi] = useState([]);
  const [jabatan, setJabatan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [del, setDel] = useState(null);
  const [search, setSearch] = useState("");
  const [filterDivisi, setFilterDivisi] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, d, j] = await Promise.all([
        api.get("/employees"), api.get("/divisi"), api.get("/jabatan")
      ]);
      setItems(e.data); setDivisi(d.data); setJabatan(j.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (it) => { setForm({ ...emptyForm, ...it }); setEditId(it.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/employees/${editId}`, form);
      else await api.post("/employees", form);
      toast.success("Data karyawan tersimpan");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/employees/${del.id}`);
      toast.success("Data karyawan dihapus"); setDel(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadFoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, foto_path: data.path });
      toast.success("Foto berhasil diupload");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setUploading(false);
  };

  const filtered = items.filter((i) => {
    if (search && !`${i.no_karyawan} ${i.nama_lengkap} ${i.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterDivisi && i.divisi_id !== filterDivisi) return false;
    if (filterStatus && i.status !== filterStatus) return false;
    return true;
  });

  const divName = (id) => divisi.find((d) => d.id === id)?.nama || "-";
  const jabName = (id) => jabatan.find((d) => d.id === id)?.nama || "-";

  return (
    <Window
      title="Management Karyawan"
      actions={
        <button className="vb-btn" onClick={openCreate} data-testid="add-karyawan-btn">
          <Plus className="w-3 h-3 inline mr-1" /> Tambah
        </button>
      }
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 200, border: "1px solid #828790", background: "white" }}>
          <Search className="w-4 h-4" style={{ marginLeft: 6 }} />
          <input
            placeholder="Cari nama / no. karyawan / email..."
            className="vb-input"
            style={{ border: "none" }}
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="search-karyawan"
          />
        </div>
        <select value={filterDivisi} onChange={(e) => setFilterDivisi(e.target.value)} className="vb-select" style={{ width: 160 }} data-testid="filter-divisi">
          <option value="">Semua Divisi</option>
          {divisi.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="vb-select" style={{ width: 160 }} data-testid="filter-status">
          <option value="">Semua Status</option>
          <option>Aktif</option>
          <option>Tidak Aktif</option>
          <option>Kontrak Selesai</option>
        </select>
      </div>

      {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message="Belum ada data karyawan" /> : (
        <div style={{ overflowX: "auto" }}>
          <table className="vb-table">
            <thead>
              <tr>
                <th>No. Karyawan</th>
                <th>Nama</th>
                <th>Email</th>
                <th>Divisi</th>
                <th>Jabatan</th>
                <th>Metode Gaji</th>
                <th>Status</th>
                <th style={{ width: 110 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id}>
                  <td>{it.no_karyawan}</td>
                  <td>{it.nama_lengkap}</td>
                  <td>{it.email}</td>
                  <td>{divName(it.divisi_id)}</td>
                  <td>{jabName(it.jabatan_id)}</td>
                  <td>
                    {it.metode_gaji === "Cash" ? (
                      <span className="vb-badge vb-badge-terlambat">Cash</span>
                    ) : (
                      <span className="vb-badge vb-badge-izin">Transfer {it.bank_nama || ""}</span>
                    )}
                  </td>
                  <td><StatusBadge status={it.status} /></td>
                  <td>
                    <button className="vb-btn" style={{ padding: "2px 6px", marginRight: 4 }} onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button className="vb-btn-danger" style={{ padding: "2px 6px" }} onClick={() => setDel(it)} data-testid={`delete-${it.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={editId ? "Edit Karyawan" : "Tambah Karyawan"}
        width={700}
        footer={
          <>
            <button className="vb-btn" onClick={() => setOpen(false)} data-testid="form-cancel">Batal</button>
            <button className="vb-btn-primary" onClick={submit} data-testid="form-save">Simpan</button>
          </>
        }
      >
        <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[
            ["no_karyawan", "No. Karyawan*", "text"],
            ["nama_lengkap", "Nama Lengkap*", "text"],
            ["tempat_lahir", "Tempat Lahir", "text"],
            ["tanggal_lahir", "Tanggal Lahir", "date"],
            ["nik", "NIK (max 16 digit)", "text"],
            ["no_hp", "No. HP (angka)", "text"],
            ["email", "Email", "email"],
            ["pendidikan", "Pendidikan Terakhir", "text"],
            ["masa_awal_masuk", "Masa Awal Masuk", "date"],
            ["tanggal_mulai_kontrak", "Mulai Kontrak", "date"],
            ["tanggal_selesai_kontrak", "Selesai Kontrak", "date"],
          ].map(([k, l, t]) => (
            <div key={k} className="vb-form-row">
              <label className="vb-label">{l}</label>
              <input type={t} className="vb-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required={l.endsWith("*")} data-testid={`form-${k}`} />
            </div>
          ))}
          <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
            <label className="vb-label">Alamat</label>
            <textarea className="vb-textarea" rows={2} value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} data-testid="form-alamat" />
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Divisi</label>
            <select className="vb-select" value={form.divisi_id} onChange={(e) => setForm({ ...form, divisi_id: e.target.value })} data-testid="form-divisi">
              <option value="">- pilih -</option>
              {divisi.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Jabatan</label>
            <select className="vb-select" value={form.jabatan_id} onChange={(e) => setForm({ ...form, jabatan_id: e.target.value })} data-testid="form-jabatan">
              <option value="">- pilih -</option>
              {jabatan.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Status</label>
            <select className="vb-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="form-status">
              <option>Aktif</option>
              <option>Tidak Aktif</option>
              <option>Kontrak Selesai</option>
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Jenis Kelamin</label>
            <select className="vb-select" value={form.jenis_kelamin} onChange={(e) => setForm({ ...form, jenis_kelamin: e.target.value })} data-testid="form-gender">
              <option>Laki-laki</option>
              <option>Perempuan</option>
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Agama</label>
            <select className="vb-select" value={form.agama} onChange={(e) => setForm({ ...form, agama: e.target.value })} data-testid="form-agama">
              <option value="">- pilih -</option>
              <option>Islam</option>
              <option>Kristen</option>
              <option>Katolik</option>
              <option>Hindu</option>
              <option>Buddha</option>
              <option>Konghucu</option>
            </select>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Foto (opsional)</label>
            <input type="file" accept="image/*" onChange={uploadFoto} disabled={uploading} className="vb-input" data-testid="form-foto" />
            {form.foto_path && <span style={{ fontSize: 11, color: "#4f8a10" }}>✓ Foto terupload</span>}
          </div>

          {/* === Metode Penggajian === */}
          <div style={{ gridColumn: "1 / 3", marginTop: 6, paddingTop: 8, borderTop: "1px dashed #828790" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#003399", marginBottom: 6 }}>Metode Penggajian</div>
          </div>
          <div className="vb-form-row">
            <label className="vb-label">Metode Gaji*</label>
            <select className="vb-select" value={form.metode_gaji} onChange={(e) => setForm({ ...form, metode_gaji: e.target.value })} required data-testid="form-metode-gaji">
              <option value="Transfer">Transfer Bank</option>
              <option value="Cash">Cash / Tunai</option>
            </select>
          </div>
          {form.metode_gaji === "Transfer" ? (
            <>
              <div className="vb-form-row">
                <label className="vb-label">Nama Bank*</label>
                <select className="vb-select" value={form.bank_nama} onChange={(e) => setForm({ ...form, bank_nama: e.target.value })} required={form.metode_gaji === "Transfer"} data-testid="form-bank-nama">
                  <option value="">- pilih bank -</option>
                  <option>BCA</option>
                  <option>BNI</option>
                  <option>BRI</option>
                  <option>Mandiri</option>
                  <option>BSI</option>
                  <option>CIMB Niaga</option>
                  <option>Permata</option>
                  <option>Danamon</option>
                  <option>BTN</option>
                  <option>Mega</option>
                  <option>OCBC NISP</option>
                  <option>Jenius</option>
                  <option>BJB</option>
                  <option>Lainnya</option>
                </select>
              </div>
              <div className="vb-form-row">
                <label className="vb-label">No. Rekening*</label>
                <input className="vb-input" value={form.bank_rekening} onChange={(e) => setForm({ ...form, bank_rekening: e.target.value.replace(/[^0-9-]/g, "") })} required={form.metode_gaji === "Transfer"} placeholder="1234567890" data-testid="form-bank-rekening" />
              </div>
              <div className="vb-form-row">
                <label className="vb-label">Atas Nama Rekening*</label>
                <input className="vb-input" value={form.bank_atas_nama} onChange={(e) => setForm({ ...form, bank_atas_nama: e.target.value })} required={form.metode_gaji === "Transfer"} placeholder="Sesuai buku tabungan" data-testid="form-bank-atas-nama" />
              </div>
            </>
          ) : (
            <div className="vb-form-row" style={{ gridColumn: "1 / 3" }}>
              <div style={{ fontSize: 12, color: "#9F6000", background: "#FEEFB3", border: "1px solid #9F6000", padding: 8, borderRadius: 2 }}>
                Pembayaran akan dilakukan secara <strong>Cash / Tunai</strong> di kantor.
              </div>
            </div>
          )}
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        onConfirm={doDelete}
        title="Hapus Karyawan"
        message={`Yakin ingin menghapus karyawan "${del?.nama_lengkap}"?`}
      />
    </Window>
  );
}
